import { useState } from "react";
import { Leaf, Sparkles, Baby } from "lucide-react";
import { MENU } from "../data/menu";
import { waLink } from "../data/site";
import { useT } from "../i18n/LanguageContext";

// Ordine categorie richiesto dal cliente
const CATEGORY_ORDER = [
  "bevande",
  "pizze-classiche",
  "pizze-speciali",
  "pizze-baby",
  "antipasti",
  "insalatone",
  "primi",
  "secondi",
  "contorni",
  "dolci",
];
const ORDERED_MENU = CATEGORY_ORDER
  .map((id) => MENU.find((c) => c.id === id))
  .filter(Boolean);

export const MenuSection = () => {
  const { t, tObj } = useT();
  const [activeId, setActiveId] = useState(ORDERED_MENU[0]?.id);
  const activeCat = ORDERED_MENU.find((c) => c.id === activeId) || ORDERED_MENU[0];

  return (
    <section id="menu" data-testid="menu-section" className="py-24 md:py-32 bg-[var(--cream)] relative">
      <div className="max-w-5xl mx-auto px-5 md:px-10">

        {/* Intro */}
        <div className="text-center mb-12 md:mb-16">
          <div className="gold-divider">
            <span className="line" />
            <span className="text-[11px] tracking-[0.3em] uppercase">{t("menu.kicker")}</span>
            <span className="line" />
          </div>
          <h2 className="mt-6 font-display text-5xl md:text-7xl text-[var(--espresso)] tracking-tight-elegant">
            {t("menu.titlePre")} <span className="italic font-serif-elegant text-[var(--gold-deep)]">{t("menu.titleEm")}</span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-[var(--espresso-soft)] text-base md:text-lg font-medium">
            {t("menu.desc")}
          </p>

          {/* Legenda icone */}
          <div className="mt-8 flex flex-wrap justify-center gap-2.5" data-testid="menu-legend">
            <Badge icon={<Leaf className="w-3.5 h-3.5" />} label={t("menu.badges.veg")} tone="basil" />
            <Badge icon={<Sparkles className="w-3.5 h-3.5" />} label={t("menu.badges.special")} tone="gold" />
            <Badge icon={<Baby className="w-3.5 h-3.5" />} label={t("menu.badges.kids")} tone="warm" />
          </div>
        </div>

        {/* Tabs categorie */}
        <div
          className="mb-12 md:mb-14 -mx-5 md:mx-0 overflow-x-auto scrollbar-hide"
          data-testid="menu-tabs"
        >
          <div className="flex gap-2 px-5 md:px-0 md:flex-wrap md:justify-center min-w-max md:min-w-0">
            {ORDERED_MENU.map((cat) => {
              const isActive = cat.id === activeId;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveId(cat.id)}
                  data-testid={`menu-tab-${cat.id}`}
                  className={`whitespace-nowrap px-5 py-3 text-[13px] md:text-[15px] tracking-[0.18em] uppercase font-semibold border transition-all duration-300 ${
                    isActive
                      ? "bg-[var(--espresso)] text-[var(--cream)] border-[var(--espresso)]"
                      : "bg-transparent text-[var(--espresso)] border-[var(--sand)] hover:border-[var(--gold)] hover:text-[var(--gold-deep)]"
                  }`}
                >
                  {tObj(cat.title)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Categoria attiva */}
        {activeCat && (
          <div key={activeCat.id} data-testid={`menu-category-${activeCat.id}`}>
            {/* Header categoria */}
            <div className="mb-10 md:mb-12 text-center">
              <div className="inline-flex items-center gap-4 mb-4">
                <span className="h-px w-12 bg-[var(--gold)]" />
                <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold-deep)] font-semibold">
                  {t("menu.categoryLabel")}
                </span>
                <span className="h-px w-12 bg-[var(--gold)]" />
              </div>
              <h3 className="font-display text-4xl md:text-6xl text-[var(--espresso)] tracking-tight-elegant">
                {tObj(activeCat.title)}
              </h3>
              {activeCat.subtitle && (
                <p className="mt-4 text-lg md:text-xl font-medium text-[var(--espresso-soft)] leading-relaxed max-w-xl mx-auto">
                  {tObj(activeCat.subtitle)}
                </p>
              )}
            </div>

            {/* Items */}
            <ul className="divide-y divide-[var(--sand)]/70 border-y border-[var(--sand)]/70 bg-[var(--ivory)]/40">
              {activeCat.items.map((item, idx) => (
                <li
                  key={`${activeCat.id}-${idx}`}
                  className="px-4 md:px-8 py-7"
                  data-testid={`menu-item-${activeCat.id}-${idx}`}
                >
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h4 className="font-display text-3xl md:text-[36px] text-[var(--espresso)] tracking-wide">
                      {item.name.toUpperCase()}
                    </h4>
                    {item.vegetarian && <Leaf className="w-5 h-5 text-[var(--basil)]" aria-label="vegetariano" />}
                    {item.signature && <Sparkles className="w-5 h-5 text-[var(--gold-deep)]" aria-label="speciale" />}
                    {item.kids && <Baby className="w-5 h-5 text-[var(--gold-deep)]" aria-label="bambini" />}
                    <span className="menu-leader" />
                    <span className="font-display text-3xl md:text-[34px] text-[var(--gold-deep)] whitespace-nowrap font-semibold">
                      € {item.price}
                    </span>
                  </div>
                  {tObj(item.desc) && (
                    <p className="mt-3 text-lg md:text-[20px] text-[var(--espresso-soft)] leading-relaxed max-w-3xl">
                      {tObj(item.desc)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Nota finale + CTA */}
        <div className="mt-20 md:mt-24 border-t border-[var(--sand)] pt-10 text-center">
          <p className="max-w-3xl mx-auto text-sm md:text-base text-[var(--warm-gray)] leading-relaxed">
            {t("menuNote")}
          </p>
          <a
            href={waLink(t("waMessages.book"))}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-10 inline-flex"
            data-testid="menu-bottom-cta"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/></svg>
            {t("menu.sideCta")}
          </a>
        </div>
      </div>
    </section>
  );
};

const Badge = ({ icon, label, tone }) => {
  const colors = {
    basil: "border-[var(--basil)]/40 text-[var(--basil)]",
    gold: "border-[var(--gold)]/50 text-[var(--gold-deep)]",
    warm: "border-[var(--warm-gray)]/40 text-[var(--warm-gray)]",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] uppercase tracking-[0.18em] font-medium ${colors[tone]}`}>
      {icon} {label}
    </span>
  );
};
